import React, { useEffect } from "react";
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputLabel,
  TextField,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import { useForm, SubmitHandler } from "react-hook-form";
import colors from "../../../../shared/themes/colors";
import Loader from "../../../../shared/components/Loader";
import { emailRegex } from "../../../../shared/validators";
import { useAppDispatch, useAppSelector } from "../../../../shared/hooks/sharedHooks";
import useTranslation from "../../../../shared/hooks/useTranslation";
import { InviteTemporaryUser } from "../../models/userModels";
import { grantTemporaryAccess } from "../../customerUsersSlice";
import { dt } from "../../../../shared/themes/redesignTokens";

interface TemporaryUserInviteDialogueProps {
  open: boolean;
  onClose: (shouldRefresh?: boolean) => void;
}

const TemporaryUserInviteDialogue = ({ open, onClose }: TemporaryUserInviteDialogueProps) => {
  const dispatch = useAppDispatch();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { translate: tAccount } = useTranslation("account");
  const { translate: tCommon } = useTranslation("common");
  const { translate: tSettings } = useTranslation("settings");

  const {
    loading,
  } = useAppSelector((state) => state.customerUsers);

  const {
    register,
    handleSubmit,
    watch,
    reset,
    formState: { errors },
  } = useForm<InviteTemporaryUser>();

  const watchedFields = watch(["userName", "firstName"]);
  const isFormValid = watchedFields.every((field) => field?.trim());

  const onSubmit: SubmitHandler<InviteTemporaryUser> = async (data: InviteTemporaryUser) => {
    const result = await dispatch(grantTemporaryAccess(data));

    if (grantTemporaryAccess.fulfilled.match(result)) {
      reset();
      onClose(true);
    }
  };

  const handleCancel = () => {
    onClose(false);
  };

  useEffect(() => {
    if (open) {
      reset();
    }
  }, [open, reset]);

  return (
    <Dialog
      open={open}
      onClose={handleCancel}
      fullScreen={isMobile}
      maxWidth={false}
      PaperProps={{
        sx: {
          ...(isMobile ? {
            margin: 0,
            borderRadius: "8px",
            maxHeight: "100vh",
            height: "100vh",
            width: "100vw",
            display: "flex",
            flexDirection: "column",
            justifyContent: "flex-start",
            padding: "16px",
          } : {
            width: "480px",
            maxWidth: "480px",
            borderRadius: "8px",
            padding: "0",
            maxHeight: "90vh",
            display: "flex",
            flexDirection: "column"
          })
        }
      }}
    >
      {loading && (
        <Box
          sx={{
            ...(isMobile ? {
              width: '100%',
              height: '100vh',
            } : {
              width: '460px',
              minHeight: '420px',
            }),
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}
        >
          <Loader />
        </Box>
      )}

      {!loading && (
        <>

          <DialogTitle
            sx={{
              textAlign: "center",
              fontFamily: "Work Sans",
              fontSize: "24px",
              fontWeight: "500",
              lineHeight: "36px",
              color: colors.secondary.gray20,
              padding: "24px 48px 16px !important",
              flex: "none",
              ...(isMobile && {
                paddingTop: "60px !important"
              })
            }}
          >
            {tSettings("inviteUserWithTemporaryAccess")}
          </DialogTitle>

          <DialogContent
            sx={{
              padding: "0 48px 24px",
              overflow: "visible"
            }}
          >
            <Box component="form" id="inviteForm" onSubmit={handleSubmit(onSubmit)}>
              <Box sx={{ display: "flex", flexDirection: "column", gap: "16px" }}>
                <Box>
                  <InputLabel
                    required
                    sx={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: colors.secondary.gray40,
                      marginBottom: "8px",
                      textTransform: "uppercase"
                    }}
                  >
                    {tAccount("email")}
                  </InputLabel>
                  <TextField
                    fullWidth
                    placeholder={tAccount("emailPlaceholder")}
                    {...register("userName", {
                      required: tCommon("forms.required"),
                      pattern: {
                        value: emailRegex,
                        message: tAccount("invalidEmail")
                      }
                    })}
                    error={!!errors.userName}
                    helperText={errors.userName ? errors.userName.message : ""}
                  />
                </Box>

                <Box>
                  <InputLabel
                    required
                    sx={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: colors.secondary.gray40,
                      marginBottom: "8px",
                      textTransform: "uppercase"
                    }}
                  >
                    {tAccount("firstName")}
                  </InputLabel>
                  <TextField
                    fullWidth
                    placeholder={tAccount("firstNamePlaceholder")}
                    {...register("firstName", {
                      required: tCommon("forms.required")
                    })}
                    error={!!errors.firstName}
                    helperText={errors.firstName ? errors.firstName.message : ""}
                  />
                </Box>

                <Box>
                  <InputLabel
                    sx={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: colors.secondary.gray40,
                      marginBottom: "8px",
                      textTransform: "uppercase"
                    }}
                  >
                    {tAccount("lastName")}
                  </InputLabel>
                  <TextField
                    fullWidth
                    placeholder={tAccount("lastNamePlaceholder")}
                    {...register("lastName")}
                    error={!!errors.lastName}
                    helperText={errors.lastName ? errors.lastName.message : ""}
                  />
                </Box>

                <Box>
                  <InputLabel
                    sx={{
                      fontSize: "12px",
                      fontWeight: "500",
                      color: colors.secondary.gray40,
                      marginBottom: "8px",
                      textTransform: "uppercase"
                    }}
                  >
                    {tSettings("visitPurpose")}
                  </InputLabel>
                  <TextField
                    fullWidth
                    placeholder={tSettings("visitPurposePlaceholder")}
                    {...register("visitPurpose", {
                      maxLength: {
                        value: 20,
                        message: tCommon("forms.maxLength", { max: 20 })
                      }
                    })}
                    error={!!errors.visitPurpose}
                    helperText={errors.visitPurpose ? errors.visitPurpose.message : ""}
                  />
                </Box>
              </Box>
            </Box>
          </DialogContent>

          <DialogActions
            sx={{
              padding: "16px 48px 24px !important",
              display: "flex",
              justifyContent: "space-between",
              gap: "12px",
              flex: "none"
            }}
          >
            <Button
              fullWidth
              variant="text"
              onClick={handleCancel}
              sx={{
                color: dt.blue,
                textTransform: "none",
                fontWeight: "500",
              }}
            >
              {tCommon("buttons.cancel")}
            </Button>
            <Button
              fullWidth
              form="inviteForm"
              type="submit"
              variant="contained"
              disabled={!isFormValid || loading}
              sx={{
                textTransform: "none",
                fontWeight: "500",
              }}
            >
              {loading ? tSettings("sending") : tSettings("sendPINCode")}
            </Button>
          </DialogActions>
        </>
      )}
    </Dialog>
  );
};

export default TemporaryUserInviteDialogue;
